import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
// Constants
import { CompanyName } from '../../utils/Constants';
// Api
import client from '../../api/client';
// Routes & API
import { HOME_PAGE_URL } from '../../utils/Routes';
import { VERIFY_EMAIL_API } from '../../utils/ApiRoutes';
// Hooks
import useNavigateToPage from '../../hooks/useNavigateToPage';
// Images
import CatLogoSvg from '../../assets/images/logos/cat-app-main-logo-svg.svg';
// Data
import {
  verifyEmailPageAdditionalMeta,
  verifyEmailPageStructuredData,
} from '../../utils/data/MetaData';
// Components
import { HelmetItem } from '../../components/utils/HelmetItem';
import AppLink from '../../components/utils/AppLink';
import SideTextComponent from '../../components/general/SideTextComponent';
import TextLogoSection from '../../components/general/TextLogoSection';
import DownloadIconSection from '../../components/general/DownloadIconSection';
import SponsorMiniBanner from '../../components/sponsor/SponsorMiniBanner';
import FiveStarComponent from '../../components/general/FiveStarComponent';

function VerifyEmailPage() {
  const { userId, uniqueString } = useParams();
  const navigateToPage = useNavigateToPage();

  // 'verifying' | 'success' | 'error'
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [redirectIn, setRedirectIn] = useState(5000000); // seconds
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const canVerify = Boolean(userId && uniqueString);

  const startRedirectCountdown = () => {
    // Toggle this to false if you *don't* want auto-redirect
    const ENABLE_AUTO_REDIRECT = true;

    if (!ENABLE_AUTO_REDIRECT) return;

    // Clear any previous timer
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setRedirectIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          navigateToPage(HOME_PAGE_URL);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const performVerification = () => {
    if (!canVerify) {
      setStatus('error');
      setMessage('Missing verification details in the link.');
      return;
    }

    setStatus('verifying');
    setMessage('');

    client
      .patch(`${VERIFY_EMAIL_API}/${userId}/${uniqueString}`)
      .then((res) => {
        if (!mountedRef.current) return;
        // If your API returns a shape, you can specialize messages here.
        setStatus('success');
        setMessage('Your email has been verified successfully.');
        startRedirectCountdown();
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.error('Unable to verify email address', err);
        setStatus('error');
        setMessage(
          'There was an issue verifying your email. Your link may be invalid or expired.'
        );
      });
  };

  useEffect(() => {
    mountedRef.current = true;
    performVerification();
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, uniqueString]); // re-run if URL params change

  const isVerifying = status === 'verifying';
  const isSuccess = status === 'success';
  const isError = status === 'error';

  return (
    <>
      {/* Tab Data */}
      <HelmetItem
        PageName='Verify Email'
        desc={`Verify your email for your ${CompanyName} account.`}
        additionalMeta={verifyEmailPageAdditionalMeta}
        structuredData={verifyEmailPageStructuredData}
      />

      {/* Page */}
      <div className='grid min-h-screen lg:h-screen lg:max-h-screen lg:overflow-hidden bg-main-background font-poppins'>
        <div className='grid w-full h-full min-h-screen lg:max-h-screen lg:overflow-hidden bg-colour2 px-1 py-1'>
          <div className='bg-colour3 rounded-2xl px-6 py-6'>
            <div className='relative grid h-full w-full items-center justify-center bg-colour1 rounded-2xl shadow-cardShadow'>
              {/* Side text */}
              <SideTextComponent />
              {/* Logo */}
              <TextLogoSection />
              {/* Download icons */}
              <DownloadIconSection />
              {/* Sponsor */}
              <SponsorMiniBanner />
              {/* 5 stars */}
              <FiveStarComponent />

              <div className='grid grid-rows-reg'>
                {/* Header + Main */}
                <div className='grid items-center justify-center h-full'>
                  <div className='grid h-fit gap-y-8'>
                    <header className='grid text-center gap-4'>
                      <figure className='grid place-items-center'>
                        <img
                          src={CatLogoSvg}
                          alt={`${CompanyName} logo`}
                          className='min-w-44 max-w-44 lg:min-w-96 lg:max-w-96 w-fit h-auto'
                        />
                      </figure>

                      <h1
                        id='verify-email-heading'
                        className='text-3xl font-bold leading-tight tracking-tight font-gladolia text-colour2 md:text-2xl dark:text-colour2'
                        aria-live='polite'
                      >
                        {isVerifying
                          ? 'Verifying Email...'
                          : isSuccess
                          ? 'Email Verified'
                          : 'Verification Failed'}
                      </h1>
                    </header>

                    {/* Main page content */}
                    <main
                      role='main'
                      className='grid w-full h-full overflow-hidden'
                    >
                      <div className='grid w-full h-full items-center py-2 px-10 overflow-hidden'>
                        <section
                          className='grid border-2 border-colour3 border-solid rounded-xl shadow-cardShadow w-full bg-colour1'
                          aria-labelledby='verify-email-heading'
                        >
                          <div className='grid grid-rows-reg gap-4 w-full h-full px-6 py-6 text-center'>
                            {/* Status / Spinner */}
                            <div
                              className='grid gap-2'
                              role={isError ? 'alert' : 'status'}
                              aria-live={isError ? 'assertive' : 'polite'}
                              aria-atomic='true'
                            >
                              {isVerifying && (
                                <div className='grid place-items-center'>
                                  {/* Tailwind spinner (no flex) */}
                                  <div
                                    className='grid place-items-center w-10 h-10 rounded-full border-4 border-colour3 border-t-transparent animate-spin'
                                    aria-hidden='true'
                                  />
                                </div>
                              )}

                              <p
                                className={
                                  isError ? 'text-error-red' : 'text-colour2'
                                }
                              >
                                {isVerifying
                                  ? 'Please wait while we confirm your email address.'
                                  : message}
                              </p>

                              {isSuccess && (
                                <p className='text-colour2'>
                                  You can continue to the homepage now.
                                  {redirectIn > 0 && (
                                    <> Redirecting in {redirectIn}s…</>
                                  )}
                                </p>
                              )}
                            </div>

                            {/* Actions */}
                            <div
                              className={`grid ${
                                isError ? 'grid-cols-2' : 'justify-center'
                              } gap-2`}
                            >
                              {isError && (
                                <button
                                  type='button'
                                  onClick={performVerification}
                                  className='grid place-items-center px-4 py-2 rounded-lg bg-colour1 text-colour2 border-2 border-solid border-colour2 active:scale-90 hover:bg-colour3 hover:text-colour1 font-bold transition duration-300 active:opacity-80'
                                  aria-label='Retry verification'
                                >
                                  Retry
                                </button>
                              )}

                              <AppLink
                                to={HOME_PAGE_URL}
                                label='Go to Home'
                                description='home page'
                                ariaLabel='Go to the home page'
                                className='grid place-items-center px-4 py-2 rounded-lg bg-colour3 font-bold active:scale-90 text-colour1 hover:bg-colour4 active:opacity-80 transition duration-300'
                                titleAttr='Home'
                              />
                            </div>

                            {/* Help text */}
                            {isError && (
                              <div className='grid'>
                                <p className='text-sm text-colour2'>
                                  If the issue persists, request a new
                                  verification link from your account page or
                                  contact support.
                                </p>
                              </div>
                            )}
                          </div>
                        </section>
                      </div>
                    </main>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default VerifyEmailPage;
